import { useState, useEffect } from 'react'

import { Sentry, logger, startSpan } from '../lib/sentry'
import { supabase } from '../lib/supabase'

import Drawer from './Drawer'

interface AddJobDrawerProps {
  isOpen: boolean
  onClose: () => void
  onJobAdded: () => void
  customContent?: React.ReactNode
}

function AddJobDrawer({ isOpen, onClose, onJobAdded, customContent }: AddJobDrawerProps) {
  const [formData, setFormData] = useState({
    title: '',
    company: '',
    location: '',
    description: '',
    url: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset form when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        title: '',
        company: '',
        location: '',
        description: '',
        url: '',
      })
      setError(null)
    }
  }, [isOpen])


  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError(null)
  }

  // Helper function to combine URL and description
  const combineUrlAndDescription = (url: string, description: string): string => {
    const trimmedDescription = description.trim()
    if (!url.trim()) {
      return trimmedDescription
    }
    
    const urlLine = `Original URL: ${url.trim()}`
    return trimmedDescription ? `${urlLine}\n\n${trimmedDescription}` : urlLine
  }

  // Helper function to insert job into database
  const insertJobToDatabase = async (finalDescription: string) => {
    const { data, error: insertError } = await supabase
      .from('jobs')
      .insert({
        title: formData.title.trim(),
        company: formData.company.trim(),
        location: formData.location.trim() || null,
        description: finalDescription || null,
        status: 'bookmarked',
      })
      .select()
      .single()

    if (insertError) {
      throw insertError
    }

    return data
  }

  // Helper function to handle insert error
  const handleInsertError = (
    insertError: { message: string; code?: string },
    span: { setAttribute?: (key: string, value: string | number) => void } | null
  ) => {
    logger.error('Failed to add job', {
      error: insertError.message,
      code: insertError.code,
      company: formData.company.trim(),
    } as Record<string, unknown>)
    Sentry.captureException(new Error(insertError.message), {
      extra: { code: insertError.code, company: formData.company.trim() },
    })
    if (span) {
      span.setAttribute('error', true)
      span.setAttribute('error_message', insertError.message)
    }
    setError(insertError.message)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.title.trim() || !formData.company.trim()) {
      setError('Job Title and Company Name are required')
      return
    }

    setIsSubmitting(true)

    await startSpan(
      {
        op: 'ui.click',
        name: 'Add Job',
      },
      async (span: { setAttribute?: (key: string, value: string | number) => void } | null) => {
        try {
          if (span) {
            span.setAttribute('company', formData.company.trim())
            span.setAttribute('has_url', !!formData.url.trim())
            span.setAttribute('has_description', !!formData.description.trim())
          }

          // Combine URL and description
          const finalDescription = combineUrlAndDescription(formData.url, formData.description)

          // Insert job
          const data = await insertJobToDatabase(finalDescription)

          if (data) {
            if (span) {
              span.setAttribute('job_id', data.id)
            }
            logger.info('Job added successfully', { jobId: data.id, title: data.title, company: data.company } as Record<string, unknown>)
          }

          onJobAdded()
          onClose()
        } catch (err) {
          if (err && typeof err === 'object' && 'message' in err && 'code' in err) {
            handleInsertError(err as { message: string; code?: string }, span)
            setIsSubmitting(false)
            return
          }
          
          const error = err instanceof Error ? err : new Error(String(err))
          logger.error('Unexpected error adding job', { error: error instanceof Error ? error.message : String(error) } as Record<string, unknown>)
          Sentry.captureException(error)
          setError(error.message || 'Failed to save job')
        } finally {
          setIsSubmitting(false)
        }
      }
    )
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Add a New Job Post"
      size="lg"
    >
      {customContent ? (
        customContent
      ) : (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Error message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Job Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Job Title
            </label>
            <input
              type="text"
              id="title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Job Title"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>

          {/* URL for Original Posting */}
          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-2">
              URL for Original Posting
            </label>
            <input
              type="text"
              id="url"
              value={formData.url}
              onChange={(e) => handleInputChange('url', e.target.value)}
              placeholder="URL for Original Posting (e.g., linkedin.com or https://linkedin.com)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Company Name */}
          <div>
            <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
              Company Name
            </label>
            <input
              type="text"
              id="company"
              value={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              placeholder="Company Name"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              required
            />
          </div>

          {/* Location */}
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
              Location
            </label>
            <input
              type="text"
              id="location"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              placeholder="Location"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>

          {/* Job Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Job Description
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Job Description"
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Saving...' : 'Save Job'}
            </button>
          </div>
        </form>
      )}
    </Drawer>
  )
}

export default AddJobDrawer

